<script setup lang="ts">
import { AdSlot, AppShell } from '@it-tools/shell'
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import BreakdownTable from './components/BreakdownTable.vue'
import DiscountsForm from './components/DiscountsForm.vue'
import PricesNote from './components/PricesNote.vue'
import ResourceList from './components/ResourceList.vue'
import TotalsPanel from './components/TotalsPanel.vue'
import TransferForm from './components/TransferForm.vue'
import { quoteAll } from './model/cost'
import { PRICES } from './model/priceBook'
import { DEFAULT_SPEC, isValidSpec, type Spec } from './model/spec'
import { readLocationSpec, replaceLocationSpec } from './model/urlState'

const { t } = useI18n()

const spec = ref<Spec>(DEFAULT_SPEC)
const shareNotice = ref(false)

const quotes = computed(() => (isValidSpec(spec.value) ? quoteAll(spec.value, PRICES) : null))

// A shared link restores the design; a malformed one silently falls back to the
// default so the visitor always lands on a working calculator.
onMounted(() => {
  const restored = readLocationSpec()
  if (restored) spec.value = restored
})

// Only a calculable design goes in the URL — a half-typed size would produce
// links that restore nothing.
watch(spec, (value) => {
  if (isValidSpec(value)) replaceLocationSpec(value)
})

async function share(): Promise<void> {
  if (isValidSpec(spec.value)) replaceLocationSpec(spec.value)
  try {
    await navigator.clipboard.writeText(window.location.href)
    shareNotice.value = true
    setTimeout(() => (shareNotice.value = false), 2000)
  } catch {
    // Clipboard access can be denied; the URL bar already holds the link.
  }
}
</script>

<template>
  <AppShell tool-name-key="tools.cloudCostCalculator">
    <template #controls>
      <span v-if="shareNotice" class="notice" role="status">{{ t('common.copied') }}</span>
    </template>

    <div class="layout">
      <div class="layout__main">
        <p class="intro">{{ t('intro') }}</p>

        <div class="calculator">
          <div class="calculator__inputs">
            <ResourceList
              class="calculator__resources"
              :model-value="spec.resources"
              @update:model-value="(resources) => (spec = { ...spec, resources })"
            />
            <TransferForm
              class="calculator__transfer"
              :model-value="spec.transfer"
              @update:model-value="(transfer) => (spec = { ...spec, transfer })"
            />
            <DiscountsForm
              class="calculator__discounts"
              :model-value="spec.discounts"
              @update:model-value="(discounts) => (spec = { ...spec, discounts })"
            />
            <div class="calculator__actions">
              <button type="button" @click="spec = DEFAULT_SPEC">{{ t('common.reset') }}</button>
              <button type="button" :disabled="!isValidSpec(spec)" @click="share()">
                {{ t('common.share') }}
              </button>
            </div>
          </div>

          <div class="calculator__results">
            <TotalsPanel class="calculator__totals" :quotes="quotes" :discounts="spec.discounts" />
          </div>
        </div>

        <BreakdownTable
          class="section"
          :spec="spec"
          :quotes="quotes"
          @update:period="(period) => (spec = { ...spec, period })"
        />
        <PricesNote class="section" />

        <p class="hint">{{ t('hint.share') }}</p>

        <AdSlot placement="leaderboard" />
      </div>

      <AdSlot placement="sidebar" />
    </div>
  </AppShell>
</template>

<style scoped>
.layout {
  display: flex;
  gap: var(--space-6);
  align-items: flex-start;
}

.layout__main {
  flex: 1;
  min-width: 0;
}

.intro {
  max-width: 46rem;
  margin: 0 0 var(--space-6);
  color: var(--color-text-muted);
}

.calculator {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(18rem, 24rem);
  gap: var(--space-4) var(--space-6);
  align-items: start;
}

.calculator__inputs {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  min-width: 0;
}

/* The answer stays in view while the resource list scrolls past. */
.calculator__results {
  position: sticky;
  top: var(--space-4);
  min-width: 0;
}

/*
 * One column on narrow screens, with the totals straight after the resources
 * they mostly depend on, rather than below every form.
 */
@media (max-width: 56rem) {
  .calculator {
    display: flex;
    flex-direction: column;
    align-items: stretch;
  }

  .calculator__inputs,
  .calculator__results {
    display: contents;
  }

  .calculator__resources {
    order: 1;
  }

  .calculator__totals {
    order: 2;
  }

  .calculator__transfer {
    order: 3;
  }

  .calculator__discounts {
    order: 4;
  }

  .calculator__actions {
    order: 5;
  }
}

.calculator__actions {
  display: flex;
  gap: var(--space-2);
}

.section {
  margin-top: var(--space-6);
}

button {
  padding: var(--space-2) var(--space-4);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface);
  color: var(--color-text);
  font: inherit;
  font-size: var(--text-sm);
  cursor: pointer;
}

button:hover:not(:disabled) {
  background: var(--color-surface-raised);
  border-color: var(--color-border-strong);
}

button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.hint {
  margin-top: var(--space-4);
  font-size: var(--text-sm);
  color: var(--color-text-muted);
}

.notice {
  font-size: var(--text-sm);
  color: var(--color-success);
}
</style>
